import React from "react";

/**
 * TextileRibbon
 * A tactile, filled textile ribbon for the Vision opening panel.
 * - Layered fabric folds with translucent gradient tone
 * - Subtle turbulence filter for a woven feel
 * - Reveals with a soft clip-path draw-in when it enters the viewport
 * - Positioned so the ribbon flows around the headline, not through it
 */
const TextileRibbon = ({ className = "", ariaHidden = true }) => {
  const uid = React.useId();
  const gradA = `${uid}-a`;
  const gradB = `${uid}-b`;
  const gradC = `${uid}-c`;
  const noise = `${uid}-noise`;
  const soft = `${uid}-soft`;

  const ref = React.useRef(null);
  const [inView, setInView] = React.useState(false);
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const m = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReduced(m.matches);
      const on = (e) => setReduced(e.matches);
      m.addEventListener?.("change", on);
      return () => m.removeEventListener?.("change", on);
    }
  }, []);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const revealed = reduced || inView;

  // The ribbon enters from the bottom, sweeps up and to the right, exits top.
  // Each layer is subtly offset for a folded look.
  const mainRibbon =
    "M 620 800 C 560 660, 720 560, 780 460 C 840 360, 700 240, 760 140 C 800 70, 900 50, 980 20 L 1080 20 C 1000 120, 880 220, 840 340 C 800 460, 900 560, 860 660 C 830 740, 720 780, 620 800 Z";

  const foldA =
    "M 640 800 C 600 700, 740 600, 800 500 C 860 400, 720 280, 780 180";
  const foldB =
    "M 660 800 C 640 720, 780 640, 820 540 C 860 440, 760 320, 810 220";

  return (
    <svg
      ref={ref}
      className={className}
      viewBox="0 0 1080 800"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
    >
      <defs>
        {/* Main ribbon body gradient — purple to green with soft mid tone */}
        <linearGradient id={gradA} x1="0.2" y1="1" x2="0.8" y2="0">
          <stop offset="0%" stopColor="#7B3FA7" stopOpacity="0.92" />
          <stop offset="45%" stopColor="#8B57BC" stopOpacity="0.85" />
          <stop offset="72%" stopColor="#7F8C7C" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#6FA84A" stopOpacity="0.9" />
        </linearGradient>

        {/* Highlight gradient — a soft sheen catching the fold */}
        <linearGradient id={gradB} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#FFFFFF" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        {/* Depth gradient — inner shadow to add dimension on the fold */}
        <linearGradient id={gradC} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#3F1A5C" stopOpacity="0.28" />
          <stop offset="55%" stopColor="#3F1A5C" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#2F5F1F" stopOpacity="0.22" />
        </linearGradient>

        {/* Woven texture — subtle turbulence displacing the ribbon surface */}
        <filter id={noise} x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="1.6"
            numOctaves="2"
            seed="7"
            result="t"
          />
          <feColorMatrix
            in="t"
            type="matrix"
            values="0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.18 0"
            result="n"
          />
          <feComposite in="n" in2="SourceGraphic" operator="in" result="masked" />
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="masked" />
          </feMerge>
        </filter>

        {/* Soft blur used for the ambient inner glow layer */}
        <filter id={soft} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="14" />
        </filter>

        {/* Draw-in mask: a rectangle that slides upward to reveal the ribbon */}
        <clipPath id={`${uid}-clip`}>
          <rect
            x="0"
            y={revealed ? "-40" : "820"}
            width="1080"
            height="900"
            style={{
              transition:
                "y 2200ms cubic-bezier(0.22, 1, 0.36, 1) 200ms",
            }}
          />
        </clipPath>
      </defs>

      {/* Ambient glow behind the ribbon — very soft, warm */}
      <g clipPath={`url(#${uid}-clip)`}>
        <path
          d={mainRibbon}
          fill="#7B3FA7"
          opacity="0.10"
          filter={`url(#${soft})`}
          transform="translate(-14 8)"
        />

        {/* Main ribbon body with woven texture */}
        <path
          d={mainRibbon}
          fill={`url(#${gradA})`}
          filter={`url(#${noise})`}
        />

        {/* Depth / inner shadow along the far fold */}
        <path d={mainRibbon} fill={`url(#${gradC})`} opacity="0.65" />

        {/* Soft sheen highlight along the leading fold */}
        <path d={mainRibbon} fill={`url(#${gradB})`} />

        {/* Fold accent line — feels like a natural crease */}
        <path
          d={foldA}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.35"
        />
        <path
          d={foldB}
          fill="none"
          stroke="#2E1440"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.22"
        />
      </g>
    </svg>
  );
};

export default TextileRibbon;
